import { analytics } from "../firebase/config";
import { logEvent, setUserId, setUserProperties } from "firebase/analytics";

// Analytics Event Types
export enum AnalyticsEvent {
  // Page Views
  PAGE_VIEW = "page_view",

  // User Engagement
  BUTTON_CLICK = "button_click",
  FORM_SUBMIT = "form_submit",
  SCROLL_DEPTH = "scroll_depth",
  TIME_ON_PAGE = "time_on_page",

  // Waitlist Specific
  WAITLIST_MODAL_OPEN = "waitlist_modal_open",
  WAITLIST_MODAL_CLOSE = "waitlist_modal_close",
  WAITLIST_SIGNUP_START = "waitlist_signup_start",
  WAITLIST_SIGNUP_SUCCESS = "waitlist_signup_success",
  WAITLIST_SIGNUP_ERROR = "waitlist_signup_error",

  // User Actions
  HERO_CTA_CLICK = "hero_cta_click",
  FOOTER_LINK_CLICK = "footer_link_click",
  MOBILE_MENU_TOGGLE = "mobile_menu_toggle",

  // Performance
  IMAGE_LOAD = "image_load",
  PAGE_LOAD_TIME = "page_load_time",
}

// Analytics Service Class
class AnalyticsService {
  private analyticsInstance: any = null;
  private consentGiven = false;
  private pageStartTime = Date.now();

  constructor() {
    this.initializeAnalytics();
  }

  private async initializeAnalytics() {
    try {
      this.analyticsInstance = await analytics;
      if (this.analyticsInstance) {
        this.trackPageView();
        this.startPerformanceTracking();
      }
    } catch (error) {
      // Analytics not supported or failed to initialize
    }
  }

  // Privacy & Consent Management
  public setConsent(consent: boolean) {
    this.consentGiven = consent;
    if (consent && this.analyticsInstance) {
      // Enable analytics tracking
      this.trackEvent(AnalyticsEvent.PAGE_VIEW, {
        event_category: "privacy",
        event_label: "consent_given",
      });
    }
  }

  public hasConsent(): boolean {
    return this.consentGiven;
  }

  // Core Tracking Methods
  public async trackEvent(eventName: string, parameters?: Record<string, any>) {
    if (!this.consentGiven || !this.analyticsInstance) {
      return;
    }

    try {
      await logEvent(this.analyticsInstance, eventName, {
        timestamp: Date.now(),
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        ...parameters,
      });
    } catch (error) {
      // Failed to track event
    }
  }

  public async trackPageView(pageTitle?: string) {
    const title = pageTitle || document.title;
    await this.trackEvent(AnalyticsEvent.PAGE_VIEW, {
      page_title: title,
      page_location: window.location.href,
      page_referrer: document.referrer,
    });
  }

  // Waitlist Conversion Tracking
  public async trackWaitlistModalOpen() {
    await this.trackEvent(AnalyticsEvent.WAITLIST_MODAL_OPEN, {
      event_category: "conversion",
      event_label: "waitlist_modal_opened",
    });
  }

  public async trackWaitlistModalClose() {
    await this.trackEvent(AnalyticsEvent.WAITLIST_MODAL_CLOSE, {
      event_category: "conversion",
      event_label: "waitlist_modal_closed",
    });
  }

  public async trackWaitlistSignupStart() {
    await this.trackEvent(AnalyticsEvent.WAITLIST_SIGNUP_START, {
      event_category: "conversion",
      event_label: "waitlist_signup_started",
    });
  }

  public async trackWaitlistSignupSuccess(email: string) {
    await this.trackEvent(AnalyticsEvent.WAITLIST_SIGNUP_SUCCESS, {
      event_category: "conversion",
      event_label: "waitlist_signup_completed",
      email_domain: email.split("@")[1], // Track email domain for insights
      timestamp: Date.now(),
    });
  }

  public async trackWaitlistSignupError(error: string) {
    await this.trackEvent(AnalyticsEvent.WAITLIST_SIGNUP_ERROR, {
      event_category: "conversion",
      event_label: "waitlist_signup_failed",
      error_message: error,
      timestamp: Date.now(),
    });
  }

  // Button Click Tracking
  public async trackButtonClick(buttonName: string, location: string) {
    await this.trackEvent(AnalyticsEvent.BUTTON_CLICK, {
      event_category: "engagement",
      event_label: buttonName,
      button_location: location,
      timestamp: Date.now(),
    });
  }

  // Form Tracking
  public async trackFormSubmit(formName: string, success: boolean) {
    await this.trackEvent(AnalyticsEvent.FORM_SUBMIT, {
      event_category: "engagement",
      event_label: formName,
      form_success: success,
      timestamp: Date.now(),
    });
  }

  // Scroll Depth Tracking
  public async trackScrollDepth(depth: number) {
    await this.trackEvent(AnalyticsEvent.SCROLL_DEPTH, {
      event_category: "engagement",
      event_label: `scroll_${depth}%`,
      scroll_percentage: depth,
      timestamp: Date.now(),
    });
  }

  // Performance Tracking
  private startPerformanceTracking() {
    // Track page load time
    window.addEventListener("load", () => {
      const loadTime = Date.now() - this.pageStartTime;
      this.trackEvent(AnalyticsEvent.PAGE_LOAD_TIME, {
        event_category: "performance",
        event_label: "page_load_complete",
        load_time_ms: loadTime,
      });
    });

    // Track scroll depth
    let maxScrollDepth = 0;
    window.addEventListener("scroll", () => {
      const scrollTop = window.pageYOffset;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      if (scrollPercent > maxScrollDepth) {
        maxScrollDepth = scrollPercent;

        // Track at key milestones
        if ([25, 50, 75, 90, 100].includes(scrollPercent)) {
          this.trackScrollDepth(scrollPercent);
        }
      }
    });

    // Track time on page
    setInterval(() => {
      const timeOnPage = Math.round((Date.now() - this.pageStartTime) / 1000);
      if (timeOnPage % 30 === 0) {
        // Track every 30 seconds
        this.trackEvent(AnalyticsEvent.TIME_ON_PAGE, {
          event_category: "engagement",
          event_label: `time_${timeOnPage}s`,
          time_on_page_seconds: timeOnPage,
        });
      }
    }, 1000);
  }

  // User Identification (for logged-in users)
  public async setUserId(userId: string) {
    if (this.analyticsInstance && this.consentGiven) {
      try {
        await setUserId(this.analyticsInstance, userId);
      } catch (error) {
        // Failed to set user ID
      }
    }
  }

  public async setUserProperties(properties: Record<string, string>) {
    if (this.analyticsInstance && this.consentGiven) {
      try {
        await setUserProperties(this.analyticsInstance, properties);
      } catch (error) {
        // Failed to set user properties
      }
    }
  }

  // Utility Methods
  public getAnalyticsInstance() {
    return this.analyticsInstance;
  }

  public isInitialized(): boolean {
    return this.analyticsInstance !== null;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;
